import * as React from 'react';

import styled, { ThemeProvider } from 'styled-components';

import { COLOR, FONT_SIZE, SPACING, THEME } from '../../packages/constants';
import { CircleWithX, CircledTick } from '../../packages/icons';
import { Button, ButtonGroup } from '../button';
import { CheckboxInput } from '../checkbox-input';
import { Text } from '../text';
import DataWrapper, { DataSource } from './atoms/DataWrapper/data-wrapper';
import { DataSignature } from './atoms/DataWrapper/data-wrapper';
import { TrackingSettingsTheme } from './tracking-settings-enums';

export interface TrackingSettingsProps {
    data: DataSignature;
    dataId: string;
    handleSaveButton: (data: DataSignature, e?: HTMLInputElement) => void;
    editAbbreviationFunction?: any;
    organisationId?: string;
    saveOnChange?: boolean;
    saveButtonLoading?: boolean;
    saveResponse?: { status: string; message: string };
    showAbbreviations?: boolean;
    showExpandCollapseAll?: boolean;
    showSearchFilter?: boolean;
    showSingleDataSource?: string;
    theme?: TrackingSettingsTheme;
    treesExpanded?: boolean;
    isLoading?: boolean;
}

const LoadingState = styled.div`
    position: absolute;
    background: rgba(0, 0, 0, 0.14);
    height: 100%;
    width: 100%;
    z-index: 1000;
`;

const StyledTrackingSettings = styled.div``;

const StyledInner = styled.div`
    margin-bottom: ${SPACING.X_LARGE};
    position: relative;
`;

const IconError = styled(CircleWithX)`
    fill: ${COLOR.PRIMARY.RED};
    height: 18px;
    width: 18px;
    margin-right: ${SPACING.X_SMALL};
`;

const IconSuccess = styled(CircledTick)`
    fill: ${COLOR.PRIMARY.GREEN};
    height: 18px;
    width: 18px;
    margin-right: ${SPACING.X_SMALL};
`;

const Message = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const Footer = styled.div`
    display: flex;
    border: ${(props) =>
        props.theme === TrackingSettingsTheme.DataGrid ? `1px solid ${COLOR.LEGACY.DATAGRID_BORDER}` : 0};
    border-top: 0;
    border-radius: ${(props) => (props.theme === TrackingSettingsTheme.DataGrid ? `0 0 3px 3px ` : 0)};
    padding: ${(props) =>
        props.theme === TrackingSettingsTheme.DataGrid ? `8px ${SPACING.MEDIUM}` : `0 0 ${SPACING.MEDIUM}`};
    background-image: ${(props) =>
        props.theme === TrackingSettingsTheme.DataGrid ? 'linear-gradient(-180deg,#f5f5f5,#f2f1f1)' : 'none'};
    box-shadow: ${(props) =>
        props.theme === TrackingSettingsTheme.DataGrid ? 'inset 0 1px 0 hsla(0,0%,100%,.5)' : 'none'};
    font-size: ${(props) => (props.theme === TrackingSettingsTheme.DataGrid ? '12px' : FONT_SIZE.LARGE)};
    line-height: ${(props) => (props.theme === TrackingSettingsTheme.DataGrid ? '16px' : FONT_SIZE.X_LARGE)};
    font-weight: 400;
`;

const StyledCheckboxListItem = styled.li`
    padding: 2px;
`;

export const TrackingSettings = ({
    data,
    dataId,
    editAbbreviationFunction,
    handleSaveButton,
    organisationId,
    saveButtonLoading,
    saveOnChange,
    saveResponse,
    showAbbreviations,
    showExpandCollapseAll = true,
    showSearchFilter,
    showSingleDataSource,
    theme,
    treesExpanded,
    isLoading,
    ...props
}: TrackingSettingsProps) => {
    const [isDisabled, setDisabled] = React.useState(true);
    const [showMessage, setShowMessage] = React.useState(true);
    const [trackingData, setTrackingData] = React.useState(data);

    React.useEffect(() => {
        setTrackingData(data);
    }, [data]);

    const updateData = (dataToUpdate: DataSignature) => {
        setTrackingData(dataToUpdate);
        setDisabled(false);
        setShowMessage(false);
    };

    const handleHideArchived = () => {
        const trackingDataCopy = { ...trackingData };
        trackingDataCopy.hideArchivedTracking = !trackingDataCopy.hideArchivedTracking;
        updateData(trackingDataCopy);
        if (saveOnChange) handleSaveButton(trackingDataCopy, undefined);
    };

    const handleDisplayUnselectedTrackingItems = () => {
        const trackingDataCopy = { ...trackingData };
        trackingDataCopy.displayUnselectedTrackingItems = !trackingDataCopy.displayUnselectedTrackingItems;
        updateData(trackingDataCopy);
        if (saveOnChange) handleSaveButton(trackingDataCopy, undefined);
    };

    React.useEffect(() => {
        if (!saveButtonLoading) {
            setDisabled(true);
        }

        if (saveResponse) {
            setShowMessage(true);

            if (saveResponse.status === 'ERROR') {
                setDisabled(false);
            }
        }
    }, [saveButtonLoading, saveResponse]);

    React.useEffect(() => {
        if (!showSingleDataSource) {
            const trackingDataCopy = { ...trackingData };
            trackingDataCopy.dataSources.sort((a, b) =>
                a.orderPriority && b.orderPriority && a.orderPriority > b.orderPriority ? 1 : -1,
            );
            setTrackingData(trackingDataCopy);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showSingleDataSource]);

    React.useEffect(() => {
        if (showSingleDataSource) {
            setTrackingData({
                dataSources: data.dataSources.filter((obj: DataSource) => obj.id === showSingleDataSource),
                hideArchivedTracking: trackingData.hideArchivedTracking,
                displayUnselectedTrackingItems: trackingData.displayUnselectedTrackingItems,
            });
        }
    }, [
        showSingleDataSource,
        data.dataSources,
        trackingData.hideArchivedTracking,
        trackingData.displayUnselectedTrackingItems,
    ]);

    const hasTracking = trackingData.dataSources.some((d) => d.hasTracking && d.categories && d.categories.length > 0);

    return (
        <ThemeProvider theme={THEME.REPORTING}>
            <StyledTrackingSettings {...props} data-test-id={dataId}>
                <StyledInner>
                    {isLoading && <LoadingState></LoadingState>}
                    {saveOnChange
                        ? trackingData.dataSources.map((dataItem: DataSource, dataIndex: number) => (
                              <DataWrapper
                                  key={`datasource-${dataIndex}`}
                                  showExpandCollapseAll={showExpandCollapseAll}
                                  showSearchFilter={showSearchFilter}
                                  data={trackingData}
                                  dropdownData={
                                      dataItem.budgets
                                          ? dataItem.budgets.sort((a: { name: string }, b: { name: string }) =>
                                                a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
                                            )
                                          : []
                                  }
                                  dataIndex={dataIndex}
                                  dataItem={dataItem}
                                  handleUpdateData={updateData}
                                  handleSaveOnChange={handleSaveButton}
                                  treesExpanded={treesExpanded}
                                  organisationId={organisationId}
                                  showAbbreviations={showAbbreviations}
                                  editAbbreviationFunction={editAbbreviationFunction}
                                  theme={theme}
                              />
                          ))
                        : trackingData.dataSources.map((dataItem: DataSource, dataIndex: number) => (
                              <DataWrapper
                                  key={`datasource-${dataIndex}`}
                                  showExpandCollapseAll={showExpandCollapseAll}
                                  showSearchFilter={showSearchFilter}
                                  data={trackingData}
                                  dropdownData={
                                      dataItem.budgets
                                          ? dataItem.budgets.sort((a: { name: string }, b: { name: string }) =>
                                                a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
                                            )
                                          : []
                                  }
                                  dataIndex={dataIndex}
                                  dataItem={dataItem}
                                  handleUpdateData={updateData}
                                  treesExpanded={treesExpanded}
                                  organisationId={organisationId}
                                  showAbbreviations={showAbbreviations}
                                  editAbbreviationFunction={editAbbreviationFunction}
                                  theme={theme}
                              />
                          ))}
                    {hasTracking && (
                        <Footer theme={theme}>
                            <ul>
                                <StyledCheckboxListItem>
                                    <CheckboxInput
                                        thinLabel
                                        dataId={'tracking-settings__checkbox-hide-archived-deleted'}
                                        name={'test'}
                                        label={'Display archived items in reports'}
                                        id={'sui-ts-hide-archived-deleted'}
                                        onChange={handleHideArchived}
                                        checked={!trackingData.hideArchivedTracking}
                                        inline
                                    />
                                </StyledCheckboxListItem>

                                <StyledCheckboxListItem>
                                    <CheckboxInput
                                        thinLabel
                                        dataId={'tracking-settings__checkbox-show-unselected-items'}
                                        name={'test'}
                                        label={"Display unselected as 'Other Items'"}
                                        id={'sui-ts-show-unselected-items'}
                                        onChange={handleDisplayUnselectedTrackingItems}
                                        checked={trackingData.displayUnselectedTrackingItems}
                                        inline
                                    />
                                </StyledCheckboxListItem>
                            </ul>
                        </Footer>
                    )}
                </StyledInner>
                {!saveOnChange && (
                    <ButtonGroup justify={'left'}>
                        <Button
                            dataId={'save'}
                            disabled={isDisabled || saveButtonLoading}
                            onClick={() => handleSaveButton(trackingData, undefined)}
                            loading={saveButtonLoading}
                        >
                            Save
                        </Button>
                        {saveResponse && showMessage && (
                            <Message>
                                {saveResponse.status === 'ERROR' && <IconError />}
                                {saveResponse.status === 'SUCCESS' && <IconSuccess />}
                                <Text marginBottom={'0'} fontSize={FONT_SIZE.X_SMALL} color={COLOR.MAIN.LABEL_TEXT}>
                                    {saveResponse.message}
                                </Text>
                            </Message>
                        )}
                    </ButtonGroup>
                )}
            </StyledTrackingSettings>
        </ThemeProvider>
    );
};
